import type { ClassroomDisciplineBotExamLangGridItemViewModel } from '@/shared/api/generated/types/ClassroomDisciplineBotExamLangGridItemViewModel';
import type { LanguageOption } from '@/shared/ui/organisms/LanguageForm';
import { decodeHtmlEntities } from '@/shared/utils/decodeHtmlEntities';
import type { RevisionLang } from '../types/revision';

/**
 * Resultado do mapeamento de status de tradução/revisão de um idioma.
 */
interface RevisionLangStatus {
  /** ID estável do status (`1` = Pendente, `2` = Revisado). */
  readonly statusId: number;
  /** Rótulo padrão em pt-BR (fallback; a UI resolve via `t()` pelo `statusId`). */
  readonly statusText: string;
}

/**
 * Mapeia o status de tradução/revisão (`EBotExamTranslateStatus`) do grid de
 * idiomas para um par `{ statusId, statusText }` estável. `2` → Revisado,
 * qualquer outro valor (incluindo ausente/desconhecido) → Pendente de revisão.
 * Aceita tanto o enum numérico (`statusEnum`) quanto o `status` textual (`"1"`|`"2"`).
 *
 * @param status - Status vindo da API (`statusEnum` numérico ou `status` textual)
 * @returns ID estável e rótulo pt-BR de fallback do status
 */
function mapLangStatus(status: string | number | null | undefined): RevisionLangStatus {
  if (status === 2 || status === '2') {
    return { statusId: 2, statusText: 'Revisado' };
  }
  return { statusId: 1, statusText: 'Pendente' };
}

/**
 * Códigos de cultura permitidos para os idiomas de uma prova em revisão,
 * conforme `CreateBotExamLangViewModel` (pt-BR, en-US, es-ES).
 */
export const ALLOWED_REVISION_CULTURES = ['pt-BR', 'en-US', 'es-ES'] as const;

/** Rótulos de exibição dos idiomas permitidos (endônimos localizados em pt-BR). */
const CULTURE_DISPLAY: Record<string, string> = {
  'pt-BR': 'Português (pt-BR)',
  'en-US': 'Inglês (en-US)',
  'es-ES': 'Espanhol (es-ES)',
};

/**
 * Resolve o rótulo de exibição de um código de cultura permitido. Culturas fora
 * do conjunto permitido retornam o próprio código.
 *
 * @param cultureInfoCode - Código BCP-47 (ex.: `en-US`)
 * @returns Rótulo de exibição do idioma
 */
export function revisionCultureDisplay(cultureInfoCode: string): string {
  return CULTURE_DISPLAY[cultureInfoCode] ?? cultureInfoCode;
}

/**
 * Converte um item do grid de idiomas (`ClassroomDisciplineBotExamLangGridItemViewModel`)
 * para o modelo de exibição `RevisionLang`. `cultureInfo` recebe o código de
 * cultura (usado para a tag/seleção) e `cultureInfoDisplay` o nome legível.
 * `questionCount` não é exposto pelo grid e assume `0`.
 *
 * @param dto - Item do grid retornado pela API
 * @returns Idioma no formato de exibição da tela de revisão
 */
export function toRevisionLang(dto: ClassroomDisciplineBotExamLangGridItemViewModel): RevisionLang {
  const cultureInfoCode = dto.cultureInfoCode ?? '';
  // `statusEnum` define o "valor" (variante) da tag; `status` define o texto exibido.
  const fallback = mapLangStatus(dto.statusEnum ?? dto.status);

  return {
    id: dto.id ?? 0,
    cultureInfo: cultureInfoCode,
    cultureInfoDisplay: dto.cultureInfo ?? revisionCultureDisplay(cultureInfoCode),
    title: decodeHtmlEntities(dto.title),
    instruction: decodeHtmlEntities(dto.instruction),
    statusId: dto.statusEnum ?? fallback.statusId,
    statusText: dto.status ?? fallback.statusText,
    questionCount: 0,
    active: dto.active ?? false,
  };
}

/**
 * Deriva no cliente os idiomas ainda disponíveis para adição: o conjunto
 * permitido (`ALLOWED_REVISION_CULTURES`) menos os já presentes na lista
 * carregada, comparando por código de cultura.
 *
 * @param existing - Idiomas já cadastrados na prova
 * @returns Opções de idioma ({ key, value }) ainda não adicionadas
 */
export function deriveAvailableLangs(existing: readonly RevisionLang[]): LanguageOption[] {
  const used = new Set(existing.map(lang => lang.cultureInfo));
  return ALLOWED_REVISION_CULTURES.filter(code => !used.has(code)).map(code => ({
    key: code,
    value: revisionCultureDisplay(code),
  }));
}
